import { useI18n } from "@/contexts/I18nContext";
import { Check, Globe } from "lucide-react";

/**
 * Reusable language picker. Used inside Settings (full grid) and inside the
 * Header dropdown (compact list). Each tile shows the language's name in
 * its NATIVE script so non-English readers can self-identify instantly.
 */
export default function LanguagePicker({ compact = false, onChosen }) {
  const { lang, setLang, t, languages } = useI18n();
  const choose = (code) => {
    setLang(code, { explicit: true });
    onChosen && onChosen(code);
  };
  return (
    <div className="w-full" data-testid="language-picker">
      {!compact && (
        <>
          <div className="text-xs uppercase tracking-[0.22em] text-[#D97706] font-semibold flex items-center gap-1.5">
            <Globe className="w-3.5 h-3.5" /> {t("settings.language.title")}
          </div>
          <p className="text-sm text-[#4B5563] mt-2 max-w-xl leading-relaxed">
            {t("settings.language.body")}
          </p>
        </>
      )}
      <div className={compact ? "mt-2 flex flex-col" : "mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3"}>
        {languages.map((l) => {
          const active = lang === l.code;
          return (
            <button
              key={l.code}
              type="button"
              data-testid={`lang-pick-${l.code}`}
              onClick={() => choose(l.code)}
              aria-pressed={active}
              className={compact
                ? `flex items-center justify-between px-3 py-2 text-sm transition ${active ? "bg-[#FFF7ED] text-[#064E3B]" : "hover:bg-[#FDFBF7] text-[#1F2937]"}`
                : `relative rounded-2xl bg-white p-4 sm:p-5 text-left transition border-2 ${active ? "border-[#064E3B] shadow-[0_10px_30px_-15px_rgba(6,78,59,0.35)]" : "border-[#E7E0D2] hover:border-[#064E3B]"}`
              }>
              {!compact && active && (
                <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[#064E3B] grid place-items-center">
                  <Check className="w-3.5 h-3.5 text-white" />
                </span>
              )}
              <div className={compact ? "flex items-center gap-2 flex-1" : ""}>
                <div className={compact
                  ? "font-medium"
                  : "font-heading text-lg text-[#1F2937]"
                } dir={l.dir}>
                  {l.native}
                </div>
                {!compact && <div className="text-xs text-[#4B5563] mt-0.5">{l.label}</div>}
              </div>
              {compact && active && <Check className="w-4 h-4 text-[#064E3B]" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
